import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, Download, Loader2, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import AdminModal from './AdminModal';
import { financeAPI } from '../services/api';
import { normalizeAdminLanguage } from '../utils/language';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';

const formatMoney = (value) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(value || 0));
const currentPeriod = () => new Date().toISOString().slice(0, 7);

const makeIdempotencyKey = async () => {
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    return [...bytes].map((item) => item.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('Secure browser random generator is unavailable');
};

export default function BankAccountManagement() {
  const { i18n } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  const language = normalizeAdminLanguage(i18n.resolvedLanguage || i18n.language);
  const text = {
    zh: {
      title: '银行卡与应付管理', subtitle: '只显示脱敏账号；完整账号需要独立权限并写入审计。',
      refresh: '刷新', search: '按用户ID、用户名或手机号搜索', account: '收款账号', owner: '用户', institution: '机构', type: '类型',
      status: '状态', actions: '操作', default: '默认', verified: '已验证', unverified: '未验证', setDefault: '设为默认',
      verify: '验证', disable: '停用', empty: '目前没有符合条件的收款账号。', loading: '正在读取脱敏资料…',
      payout: '应付与结算', period: '月份', role: '结算身份', calculate: '计算应付', reserve: '建立结算锁定',
      payable: '可申请', earned: '累计已赚', reserved: '已锁定', paid: '已支付', noDefault: '该用户没有可用默认收款账号。',
      payouts: '结算记录', approve: '批准', reject: '拒绝', markPaid: '标记已支付', details: '详情', error: '操作失败，请检查输入或权限。',
      stewardRole: '水管家', payableUserId: '应付与结算用户ID', previous: '上一页', next: '下一页', pageSummary: '第 {{page}} / {{pages}} 页，共 {{total}} 条',
      reveal: '显示人工转账资料', hide: '立即隐藏', holder: '开户名', manualOnly: '仅供财务人员人工转账使用；系统不会自动汇款。', expires: '敏感资料会在 30 秒后自动隐藏。'
    },
    id: {
      title: 'Rekening Bank & Hutang', subtitle: 'Nomor selalu disamarkan; akses penuh memerlukan izin dan audit terpisah.',
      refresh: 'Muat ulang', search: 'Cari ID, nama, atau nomor telepon pengguna', account: 'Tujuan', owner: 'Pengguna', institution: 'Institusi', type: 'Tipe',
      status: 'Status', actions: 'Aksi', default: 'Utama', verified: 'Terverifikasi', unverified: 'Belum verifikasi', setDefault: 'Jadikan utama',
      verify: 'Verifikasi', disable: 'Nonaktifkan', empty: 'Tidak ada rekening yang cocok.', loading: 'Memuat data tersamar…',
      payout: 'Hutang & penyelesaian', period: 'Periode', role: 'Peran', calculate: 'Hitung', reserve: 'Buat reservasi', payable: 'Dapat dibayar',
      earned: 'Pendapatan', reserved: 'Direservasi', paid: 'Dibayar', noDefault: 'Tidak ada rekening utama aktif.',
      payouts: 'Riwayat penyelesaian', approve: 'Setujui', reject: 'Tolak', markPaid: 'Tandai dibayar', details: 'Detail', error: 'Operasi gagal. Periksa input atau izin.',
      stewardRole: 'Pengelola', payableUserId: 'ID pengguna untuk penyelesaian', previous: 'Sebelumnya', next: 'Berikutnya', pageSummary: 'Halaman {{page}} / {{pages}}, total {{total}}',
      reveal: 'Tampilkan data transfer manual', hide: 'Sembunyikan sekarang', holder: 'Nama pemilik', manualOnly: 'Hanya untuk transfer manual oleh tim keuangan; sistem tidak mengirim dana otomatis.', expires: 'Data sensitif disembunyikan otomatis setelah 30 detik.'
    },
    en: {
      title: 'Bank Accounts & Payables', subtitle: 'Accounts are masked; revealing a full value requires a separate permission and audit event.',
      refresh: 'Refresh', search: 'Search user ID, name, or phone', account: 'Destination', owner: 'User', institution: 'Institution', type: 'Type', status: 'Status', actions: 'Actions',
      default: 'Default', verified: 'Verified', unverified: 'Unverified', setDefault: 'Set default', verify: 'Verify', disable: 'Disable',
      empty: 'No matching beneficiary accounts.', loading: 'Loading masked records…', payout: 'Payable & settlements', period: 'Period', role: 'Account role',
      calculate: 'Calculate', reserve: 'Create reservation', payable: 'Payable', earned: 'Earned', reserved: 'Reserved', paid: 'Paid', noDefault: 'No active default account for this user.',
      payouts: 'Settlement history', approve: 'Approve', reject: 'Reject', markPaid: 'Mark paid', details: 'Details', error: 'Operation failed. Check the input or permission.',
      stewardRole: 'Steward', payableUserId: 'Payable settlement user ID', previous: 'Previous', next: 'Next', pageSummary: 'Page {{page}} / {{pages}}, {{total}} total',
      reveal: 'Show manual transfer details', hide: 'Hide now', holder: 'Account holder', manualOnly: 'For manual transfer by finance staff only; the system never sends funds automatically.', expires: 'Sensitive details are hidden automatically after 30 seconds.'
    }
  }[language] || {};

  const [accounts, setAccounts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [debouncedUserFilter, setDebouncedUserFilter] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const [accountPagination, setAccountPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [selected, setSelected] = useState(null);
  const [revealedAccount, setRevealedAccount] = useState(null);
  const [revealingAccount, setRevealingAccount] = useState(false);
  const [payableQuery, setPayableQuery] = useState({ userId: '', accountType: 'PUSKUD', periodKey: currentPeriod() });
  const [payable, setPayable] = useState(null);
  const canManageBankAccounts = hasAdminPermission(currentUser, 'manage_bank_accounts');
  const canRevealBankAccounts = hasAdminPermission(currentUser, 'reveal_bank_accounts');
  const canViewPayouts = hasAdminPermission(currentUser, 'view_payouts');
  const canManagePayouts = hasAdminPermission(currentUser, 'manage_payouts');
  const canApprovePayouts = hasAdminPermission(currentUser, 'approve_payouts');

  const settlementRoleOptions = [
    { value: 'PUSKUD', label: 'RP' },
    { value: 'KUD', label: 'PA' },
    { value: 'Steward', label: text.stewardRole }
  ];

  const getBusinessRoleLabels = (user) => {
    const roles = new Set([...(user?.roles || []), user?.primaryRole].filter(Boolean));
    const labels = [];
    if (roles.has('PUSKUD')) labels.push('RP');
    if (roles.has('KUD') || roles.has('RP')) labels.push('PA');
    if (roles.has('Steward')) labels.push(text.stewardRole);
    return labels;
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAccountPage(1);
      setDebouncedUserFilter(userFilter.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [userFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...(debouncedUserFilter ? { q: debouncedUserFilter } : {}),
        page: accountPage,
        limit: 20
      };
      const [accountResponse, payoutResponse] = await Promise.all([
        financeAPI.getBankAccounts(params),
        canViewPayouts ? financeAPI.getPayouts({}) : Promise.resolve(null)
      ]);
      setAccounts(accountResponse.data?.data || []);
      setAccountPagination(accountResponse.data?.pagination || { page: accountPage, limit: 20, total: 0, totalPages: 1 });
      setPayouts(payoutResponse?.data?.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.error);
    } finally {
      setLoading(false);
    }
  }, [accountPage, canViewPayouts, debouncedUserFilter, text.error]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setRevealedAccount(null);
  }, [selected]);

  useEffect(() => {
    if (!revealedAccount) return undefined;
    const timer = window.setTimeout(() => setRevealedAccount(null), 30000);
    return () => window.clearTimeout(timer);
  }, [revealedAccount]);

  const defaultsByUser = useMemo(() => new Map(accounts.filter((item) => item.isDefault && item.status === 'Active').map((item) => [String(item.userId), item])), [accounts]);

  const actOnAccount = async (account, action) => {
    if (!canManageBankAccounts) return;
    setError('');
    try {
      if (action === 'default') await financeAPI.setDefaultBankAccount(account.id);
      if (action === 'verify') await financeAPI.verifyBankAccount(account.id);
      if (action === 'disable') await financeAPI.updateBankAccount(account.id, { version: account.version, status: 'Inactive' });
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.error);
    }
  };

  const calculate = async () => {
    if (!canViewPayouts) return;
    setError('');
    try {
      const response = await financeAPI.getPayable({ ...payableQuery, userId: Number(payableQuery.userId) });
      setPayable(response.data?.data || null);
    } catch (requestError) {
      setPayable(null);
      setError(requestError.response?.data?.message || text.error);
    }
  };

  const reserve = async () => {
    if (!canManagePayouts) return;
    const defaultAccount = defaultsByUser.get(String(payableQuery.userId));
    if (!defaultAccount) return setError(text.noDefault);
    try {
      await financeAPI.createPayout({
        ...payableQuery,
        userId: Number(payableQuery.userId),
        bankAccountId: defaultAccount.id,
        idempotencyKey: await makeIdempotencyKey()
      });
      setPayable(null);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.error);
    }
  };

  const transitionPayout = async (payout, status) => {
    if (!canApprovePayouts) return;
    const reason = status === 'Rejected' ? window.prompt(`${text.reject}:`) : '';
    if (status === 'Rejected' && reason === null) return;
    try {
      await financeAPI.transitionPayout(payout.id, { status, reason });
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.error);
    }
  };

  const downloadMasked = async () => {
    try {
      const response = await financeAPI.exportBankAccounts();
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ATMWater_Beneficiary_Accounts_Masked.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.error);
    }
  };

  const revealForManualTransfer = async () => {
    if (!selected || !canRevealBankAccounts) return;
    setRevealingAccount(true);
    setError('');
    try {
      const response = await financeAPI.revealBankAccount(selected.id);
      setRevealedAccount(response.data?.data || null);
    } catch (requestError) {
      setRevealedAccount(null);
      setError(requestError.response?.data?.message || text.error);
    } finally {
      setRevealingAccount(false);
    }
  };

  return (
    <div className="admin-page min-h-screen space-y-6 p-4 md:p-7">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="admin-text-primary flex items-center gap-3 text-2xl font-black md:text-3xl"><Banknote className="text-cyan-500" />{text.title}</h1>
          <p className="admin-text-secondary mt-2 max-w-3xl text-sm">{text.subtitle}</p>
        </div>
      </header>

      {error && <div role="alert" className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm font-bold text-rose-500">{error}</div>}

      <section className="admin-card rounded-2xl p-4 md:p-5">
        <div className="flex flex-wrap items-end gap-3">
          <label className="min-w-[220px] flex-1 text-xs font-bold uppercase tracking-wide">{text.search}
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-400/25 px-3"><Search size={16} /><input type="search" aria-label={text.search} value={userFilter} onChange={(event) => setUserFilter(event.target.value)} className="admin-input w-full border-0 bg-transparent py-2 outline-none" /></div>
          </label>
          <button type="button" onClick={load} className="admin-button-secondary rounded-xl px-4 py-2"><RefreshCw size={16} className="mr-2 inline" />{text.refresh}</button>
          <button type="button" onClick={downloadMasked} className="admin-button-secondary rounded-xl px-4 py-2" aria-label="Download masked bank accounts"><Download size={16} /></button>
        </div>
        {loading ? <div className="flex items-center justify-center gap-3 py-14"><Loader2 className="animate-spin" />{text.loading}</div> : accounts.length === 0 ? <p className="admin-text-secondary py-14 text-center">{text.empty}</p> : (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="admin-text-secondary border-b border-slate-400/20 text-xs uppercase"><tr><th className="p-3">{text.owner}</th><th className="p-3">{text.account}</th><th className="p-3">{text.institution}</th><th className="p-3">{text.status}</th>{canManageBankAccounts && <th className="p-3 text-right">{text.actions}</th>}</tr></thead>
              <tbody>{accounts.map((account) => <tr key={account.id} className="border-b border-slate-400/10">
                <td className="p-3"><strong className="admin-text-primary block">{account.user?.displayName || `#${account.userId}`}</strong><div className="admin-text-secondary mt-1 flex flex-wrap gap-2 text-xs"><span>#{account.userId}</span>{account.user?.maskedPhone && <span>{account.user.maskedPhone}</span>}</div><div className="mt-1 flex flex-wrap gap-1">{getBusinessRoleLabels(account.user).map((label) => <span key={label} className="rounded-full bg-cyan-500/10 px-2 py-0.5 text-xs font-bold text-cyan-600">{label}</span>)}</div></td><td className="p-3"><button type="button" className="font-mono font-black text-cyan-500" onClick={() => setSelected(account)}>{account.maskedAccountNumber}</button><div className="admin-text-secondary text-xs">{account.destinationType}</div></td>
                <td className="p-3">{account.institutionCode}</td><td className="p-3"><div className="flex flex-wrap gap-2">{account.isDefault && <span className="rounded-full bg-cyan-500/15 px-2 py-1 text-xs text-cyan-500">{text.default}</span>}<span className={`rounded-full px-2 py-1 text-xs ${account.status === 'Active' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-slate-500/15'}`}>{account.status}</span><span className="text-xs">{account.verified ? text.verified : text.unverified}</span></div></td>
                {canManageBankAccounts && <td className="p-3 text-right"><div className="flex justify-end gap-2">{!account.isDefault && account.status === 'Active' && <button type="button" onClick={() => actOnAccount(account, 'default')} className="admin-button-secondary rounded-lg px-3 py-1 text-xs">{text.setDefault}</button>}{!account.verified && account.status === 'Active' && <button type="button" onClick={() => actOnAccount(account, 'verify')} className="admin-button-secondary rounded-lg px-3 py-1 text-xs">{text.verify}</button>}{account.status === 'Active' && <button type="button" onClick={() => actOnAccount(account, 'disable')} className="rounded-lg border border-rose-500/30 px-3 py-1 text-xs text-rose-500">{text.disable}</button>}</div></td>}
              </tr>)}</tbody>
            </table>
          </div>
        )}
        {!loading && (
          <div className="admin-text-secondary mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span>{text.pageSummary.replace('{{page}}', accountPagination.page).replace('{{pages}}', Math.max(accountPagination.totalPages || 1, 1)).replace('{{total}}', accountPagination.total)}</span>
            <div className="flex gap-2"><button type="button" disabled={accountPage <= 1} onClick={() => setAccountPage((page) => page - 1)} className="admin-button-secondary rounded-lg px-3 py-1.5 disabled:opacity-40">{text.previous}</button><button type="button" disabled={accountPage >= Math.max(accountPagination.totalPages || 1, 1)} onClick={() => setAccountPage((page) => page + 1)} className="admin-button-secondary rounded-lg px-3 py-1.5 disabled:opacity-40">{text.next}</button></div>
          </div>
        )}
      </section>

      {canViewPayouts && <section className="admin-card rounded-2xl p-4 md:p-5">
        <h2 className="admin-text-primary text-xl font-black">{text.payout}</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <label className="text-xs font-bold">{text.owner}<input aria-label={text.payableUserId} value={payableQuery.userId} onChange={(event) => setPayableQuery({ ...payableQuery, userId: event.target.value.replace(/\D/g, '') })} className="admin-input mt-2 w-full rounded-xl border border-slate-400/25 bg-transparent p-2" /></label>
          <label className="text-xs font-bold">{text.role}<select aria-label={text.role} value={payableQuery.accountType} onChange={(event) => setPayableQuery({ ...payableQuery, accountType: event.target.value })} className="admin-input mt-2 w-full rounded-xl border border-slate-400/25 bg-transparent p-2">{settlementRoleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label className="text-xs font-bold">{text.period}<input type="month" value={payableQuery.periodKey} onChange={(event) => setPayableQuery({ ...payableQuery, periodKey: event.target.value })} className="admin-input mt-2 w-full rounded-xl border border-slate-400/25 bg-transparent p-2" /></label>
          <button type="button" disabled={!payableQuery.userId} onClick={calculate} className="mt-auto rounded-xl bg-cyan-500 px-4 py-2 font-black text-slate-950 disabled:opacity-40">{text.calculate}</button>
        </div>
        {payable && <div className="admin-muted-panel mt-4 grid gap-3 rounded-xl p-4 sm:grid-cols-4"><div><span className="text-xs">{text.earned}</span><strong className="block">{formatMoney(payable.earned)}</strong></div><div><span className="text-xs">{text.reserved}</span><strong className="block">{formatMoney(payable.reserved)}</strong></div><div><span className="text-xs">{text.paid}</span><strong className="block">{formatMoney(payable.paid)}</strong></div><div><span className="text-xs">{text.payable}</span><strong className="block text-cyan-500">{formatMoney(payable.payable)}</strong></div>{payable.payable > 0 && canManagePayouts && <button type="button" onClick={reserve} className="sm:col-span-4 rounded-xl border border-cyan-500 px-4 py-2 font-black text-cyan-500">{text.reserve}</button>}</div>}

        <h3 className="admin-text-primary mt-7 font-black">{text.payouts}</h3>
        <div className="mt-3 space-y-3">{payouts.map((payout) => <article key={payout.id} className="admin-muted-panel flex flex-wrap items-center justify-between gap-3 rounded-xl p-4"><div><strong>#{payout.id} · {formatMoney(payout.amount)}</strong><p className="admin-text-secondary text-xs">#{payout.userId} · {payout.accountType} · {payout.periodKey} · {payout.institutionCode} {payout.maskedDestination}</p></div><div className="flex flex-wrap items-center gap-2"><span className="rounded-full bg-slate-500/15 px-3 py-1 text-xs font-bold">{payout.status}</span>{canApprovePayouts && payout.status === 'Pending' && <><button type="button" onClick={() => transitionPayout(payout, 'Approved')} className="rounded-lg border border-emerald-500/30 px-3 py-1 text-xs text-emerald-500">{text.approve}</button><button type="button" onClick={() => transitionPayout(payout, 'Rejected')} className="rounded-lg border border-rose-500/30 px-3 py-1 text-xs text-rose-500">{text.reject}</button></>}{canApprovePayouts && ['Approved', 'Processing'].includes(payout.status) && <button type="button" onClick={() => transitionPayout(payout, 'Paid')} className="rounded-lg border border-cyan-500/30 px-3 py-1 text-xs text-cyan-500">{text.markPaid}</button>}</div></article>)}</div>
      </section>}

      <AdminModal open={Boolean(selected)} onClose={() => setSelected(null)} title={text.details} size="sm">
        {selected && <div className="space-y-4"><dl className="grid grid-cols-2 gap-4 text-sm"><dt className="admin-text-secondary">ID</dt><dd>#{selected.id}</dd><dt className="admin-text-secondary">{text.owner}</dt><dd>#{selected.userId}</dd><dt className="admin-text-secondary">{text.account}</dt><dd className="font-mono">{revealedAccount?.accountNumber || selected.maskedAccountNumber}</dd>{revealedAccount?.accountHolder && <><dt className="admin-text-secondary">{text.holder}</dt><dd>{revealedAccount.accountHolder}</dd></>}<dt className="admin-text-secondary">{text.institution}</dt><dd>{selected.institutionCode}</dd><dt className="admin-text-secondary">{text.status}</dt><dd>{selected.status}</dd><dt className="admin-text-secondary">Version</dt><dd>{selected.version}</dd><dt className="admin-text-secondary">Security</dt><dd className="flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-500" />AES-GCM</dd></dl>{canRevealBankAccounts && <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs"><p className="font-bold text-amber-600">{text.manualOnly}</p><p className="admin-text-secondary mt-1">{text.expires}</p><button type="button" disabled={revealingAccount} onClick={revealedAccount ? () => setRevealedAccount(null) : revealForManualTransfer} className="admin-button-secondary mt-3 rounded-lg px-3 py-2 font-bold disabled:opacity-50">{revealedAccount ? text.hide : text.reveal}</button></div>}</div>}
      </AdminModal>
    </div>
  );
}
